'use client'

import { useCallback, useState } from 'react'

/**
 * Enhanced Set hook with proper reference updates on changes.
 * Unlike @uidotdev/usehooks, this implementation updates the Set reference when elements are added/removed,
 * ensuring proper React re-renders.
 * @param iterable - Optional initial values for the Set
 * @returns Tuple of [state, add, remove, toggle, set] functions
 */
export const useSet = <T>(
  iterable?: Iterable<T> | null
): [
  Set<T>,
  add: (...values: T[]) => void,
  remove: (...values: T[]) => void,
  toggle: (value: T, toggled?: boolean) => void,
  set: (iterable?: Iterable<T> | null) => void
] => {
  const [state, setState] = useState(() => new Set(iterable))

  const add = useCallback((...values: T[]) => {
    setState(prev => {
      const newSet = new Set(prev)
      let changed = false
      values.forEach(v => {
        if (!newSet.has(v)) {
          newSet.add(v)
          changed = true
        }
      })
      return changed ? newSet : prev
    })
  }, [])

  const remove = useCallback((...values: T[]) => {
    setState(prev => {
      const newSet = new Set(prev)
      let changed = false
      values.forEach(v => {
        if (newSet.delete(v)) {
          changed = true
        }
      })
      return changed ? newSet : prev
    })
  }, [])

  const toggle = useCallback((value: T, toggled?: boolean) => {
    setState(prev => {
      const shouldInclude = toggled ?? !prev.has(value)
      if (shouldInclude === prev.has(value)) return prev

      const newSet = new Set(prev)
      if (shouldInclude) newSet.add(value)
      else newSet.delete(value)
      return newSet
    })
  }, [])

  const set = useCallback((iterable?: Iterable<T> | null) => {
    setState(prev => {
      const newSet = new Set(iterable)
      if (prev.size === newSet.size && Array.from(prev).every(item => newSet.has(item))) {
        return prev
      }
      return newSet
    })
  }, [])

  return [state, add, remove, toggle, set]
}
